import { useEffect, useState } from 'react'
import { useController, UseControllerProps } from 'react-hook-form'

import { RadioGroup } from '@headlessui/react'
import { ExclamationCircleIcon } from '@heroicons/react/24/outline'

export interface InlineRadioData {
  label: string
  value: string | number
  disabled?: boolean
}

interface InlineRadioInterface {
  label?: string
  data: InlineRadioData[]
  adjustContent?: boolean
  inline?: string
}
function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ')
}

export default function InlineRadio(
  props: UseControllerProps & InlineRadioInterface
) {
  const { data, label, defaultValue, adjustContent } = props
  const { field, fieldState } = useController(props)
  const [selected, setSelected] = useState<InlineRadioData | null>(null)

  useEffect(() => {
    if (defaultValue) {
      setSelected(data.find((item) => item.value === defaultValue) ?? null)
      field.onChange(defaultValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onChange = (e: InlineRadioData) => {
    setSelected(e)
    field.onChange(e.value)
  }

  return (
    <>
      <div className="w-full py-2">
        {label && (
          <div className="mb-3 flex items-center">
            <h2 className="text-md font-medium leading-6 text-gray-900">
              {label}
            </h2>
          </div>
        )}

        <RadioGroup
          by={(y, z) => y?.value === z?.value}
          onChange={onChange}
          value={selected}
        >
          <div className="flex w-full flex-shrink-0 flex-wrap justify-center gap-3">
            {data.map((option, idx) => (
              <RadioGroup.Option
                className={({ active, checked }) =>
                  classNames(
                    !option.disabled
                      ? 'cursor-pointer focus:outline-none'
                      : 'cursor-not-allowed opacity-25',
                    active ? 'ring-2 ring-slate-600 ring-offset-2' : '',
                    checked
                      ? 'bg-slate-600 text-white hover:bg-slate-500'
                      : 'bg-white text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50',
                    `flex flex-shrink-0 items-center justify-center rounded-md px-3 py-2 text-sm font-semibold sm:${
                      adjustContent ? 'flex-shrink' : 'flex-1'
                    }`
                  )
                }
                disabled={option.disabled}
                key={idx}
                value={option}
              >
                <RadioGroup.Label as="span">{option.label}</RadioGroup.Label>
              </RadioGroup.Option>
            ))}
          </div>
        </RadioGroup>

        <div className="relative inline-flex w-full items-center rounded-md shadow-sm">
          {fieldState.error && (
            <>
              <div className="pointer-events-none absolute inset-y-0 right-0 top-2 pr-3">
                <ExclamationCircleIcon
                  aria-hidden="true"
                  className="h-5 w-5 text-red-500"
                />
              </div>

              <p className="absolute -bottom-5 flex flex-shrink-0 text-xs text-red-600">
                {fieldState.error?.message}
              </p>
            </>
          )}
        </div>
      </div>
    </>
  )
}
